import panzoom from 'panzoom';

type UsePanZoomParameters = {
    blockClass: string;
    mapElement: HTMLDivElement;
};

type PanZoomInstance = {
    dispose?: () => void;
    getTransform: () => { scale: number; x: number; y: number };
    moveTo: (horizontalOffset: number, verticalOffset: number) => void;
    on: (eventName: string, callback: () => void) => void;
    pause?: () => void;
    resume?: () => void;
    zoomAbs: (horizontalPosition: number, verticalPosition: number, scale: number) => void;
    zoomTo: (horizontalPosition: number, verticalPosition: number, scaleMultiplier: number) => void;
};

const usePanZoom = ({
    blockClass,
    mapElement,
}: UsePanZoomParameters) => {
    const panZoomContainerElement = mapElement.querySelector<HTMLDivElement>(`.${blockClass}__panzoom-container`);
    const controlButtonsElement = mapElement.querySelector<HTMLDivElement>(`.${blockClass}__panzoom-control-buttons`);

    if (!panZoomContainerElement || !controlButtonsElement) {
        return {
            isActivationSuppressed: (): boolean => false,
        };
    }

    const dragThresholdInPixels = 5;
    let panZoomInstance: PanZoomInstance | null = null;
    let panIsEnabled = false;
    let resizeTimeoutId: number | null = null;
    let transformFrameId: number | null = null;
    let lastVisualCenter: { horizontalCenter: number; verticalCenter: number } | null = null;
    let pointerIsPressed = false;
    let pointerHasMoved = false;
    let pointerStartHorizontal = 0;
    let pointerStartVertical = 0;
    let suppressActivationUntilTimestamp = 0;

    const setPanEnabled = (isPanEnabled: boolean): void => {
        if (!panZoomInstance || panIsEnabled === isPanEnabled) {
            return;
        }

        panIsEnabled = isPanEnabled;

        if (isPanEnabled) {
            panZoomInstance.resume?.();
            panZoomContainerElement.classList.add(`${blockClass}__panzoom-container--can-drag`);
            panZoomContainerElement.style.touchAction = 'none';
            mapElement.style.touchAction = 'none';

            return;
        }

        panZoomInstance.pause?.();
        panZoomContainerElement.classList.remove(`${blockClass}__panzoom-container--can-drag`);
        panZoomContainerElement.style.touchAction = 'pan-y pinch-zoom';
        mapElement.style.touchAction = 'pan-y pinch-zoom';
    };

    const getClampedOffsets = (scale: number, x: number, y: number): { horizontalOffset: number; verticalOffset: number } => {
        const viewportWidth = mapElement.clientWidth;
        const viewportHeight = mapElement.clientHeight;
        const contentWidth = panZoomContainerElement.offsetWidth * scale;
        const contentHeight = panZoomContainerElement.offsetHeight * scale;

        const minimumHorizontalOffset = Math.min(0, viewportWidth - contentWidth);
        const maximumHorizontalOffset = 0;
        const minimumVerticalOffset = Math.min(0, viewportHeight - contentHeight);
        const maximumVerticalOffset = 0;

        return {
            horizontalOffset: Math.min(maximumHorizontalOffset, Math.max(minimumHorizontalOffset, x)),
            verticalOffset: Math.min(maximumVerticalOffset, Math.max(minimumVerticalOffset, y)),
        };
    };

    const centerMap = (scale: number): void => {
        if (!panZoomInstance) {
            return;
        }

        const viewportWidth = mapElement.clientWidth;
        const viewportHeight = mapElement.clientHeight;
        const contentWidth = panZoomContainerElement.offsetWidth * scale;
        const contentHeight = panZoomContainerElement.offsetHeight * scale;
        const horizontalOffset = (viewportWidth - contentWidth) / 2;
        const verticalOffset = (viewportHeight - contentHeight) / 2;

        panZoomInstance.moveTo(horizontalOffset, verticalOffset);
    };

    const getVisualCenter = (): { horizontalCenter: number; verticalCenter: number } | null => {
        if (!panZoomInstance) {
            return null;
        }

        const { scale, x, y } = panZoomInstance.getTransform();

        return {
            horizontalCenter: (mapElement.clientWidth / 2 - x) / scale,
            verticalCenter: (mapElement.clientHeight / 2 - y) / scale,
        };
    };

    const restoreVisualCenterAfterResize = (): void => {
        if (!panZoomInstance || !lastVisualCenter) {
            return;
        }

        const { scale } = panZoomInstance.getTransform();

        if (scale <= 1) {
            return;
        }

        const horizontalOffset = mapElement.clientWidth / 2 - lastVisualCenter.horizontalCenter * scale;
        const verticalOffset = mapElement.clientHeight / 2 - lastVisualCenter.verticalCenter * scale;

        panZoomInstance.moveTo(horizontalOffset, verticalOffset);

        const clampedOffsets = getClampedOffsets(scale, horizontalOffset, verticalOffset);

        if (clampedOffsets.horizontalOffset !== horizontalOffset || clampedOffsets.verticalOffset !== verticalOffset) {
            panZoomInstance.moveTo(clampedOffsets.horizontalOffset, clampedOffsets.verticalOffset);
        }
    };

    const zoomToViewportCenter = (direction: 'in' | 'out'): void => {
        if (!panZoomInstance) {
            return;
        }

        setPanEnabled(true);

        const horizontalCenter = mapElement.clientWidth / 2;
        const verticalCenter = mapElement.clientHeight / 2;
        const scaleMultiplier = direction === 'in' ? 1.5 : 0.7;

        panZoomInstance.zoomTo(horizontalCenter, verticalCenter, scaleMultiplier);

        const { scale } = panZoomInstance.getTransform();

        if (scale <= 1) {
            setPanEnabled(false);
            centerMap(scale);

            return;
        }

        const { x, y } = panZoomInstance.getTransform();
        const clampedOffsets = getClampedOffsets(scale, x, y);

        if (clampedOffsets.horizontalOffset !== x || clampedOffsets.verticalOffset !== y) {
            panZoomInstance.moveTo(clampedOffsets.horizontalOffset, clampedOffsets.verticalOffset);
        }

        lastVisualCenter = getVisualCenter();
    };

    const resetZoom = (): void => {
        if (!panZoomInstance) {
            return;
        }

        panZoomInstance.zoomAbs(0, 0, 1);

        window.setTimeout(() => {
            centerMap(1);
            setPanEnabled(false);
        }, 50);
    };

    const syncTransformState = (): void => {
        if (!panZoomInstance) {
            return;
        }

        const { scale, x, y } = panZoomInstance.getTransform();

        setPanEnabled(scale > 1);

        if (scale <= 1) {
            centerMap(scale);
            return;
        }

        lastVisualCenter = getVisualCenter();

        const clampedOffsets = getClampedOffsets(scale, x, y);

        if (clampedOffsets.horizontalOffset !== x || clampedOffsets.verticalOffset !== y) {
            panZoomInstance.moveTo(clampedOffsets.horizontalOffset, clampedOffsets.verticalOffset);
        }
    };

    const scheduleTransformSync = (): void => {
        if (transformFrameId !== null) {
            return;
        }

        transformFrameId = window.requestAnimationFrame(() => {
            transformFrameId = null;
            syncTransformState();
        });
    };

    const bindControlButton = (action: 'zoom-in' | 'zoom-out' | 'reset-zoom', handler: () => void): void => {
        const controlButtonElement = controlButtonsElement.querySelector<HTMLButtonElement>(`[data-action='${action}']`);

        if (!controlButtonElement) {
            return;
        }

        controlButtonElement.addEventListener('click', (event) => {
            event.stopPropagation();
            handler();
        });
    };

    const handlePointerDown = (event: PointerEvent): void => {
        if (controlButtonsElement.contains(event.target as Node)) {
            return;
        }

        pointerIsPressed = true;
        pointerHasMoved = false;
        pointerStartHorizontal = event.clientX;
        pointerStartVertical = event.clientY;
    };

    const handlePointerMove = (event: PointerEvent): void => {
        if (!pointerIsPressed) {
            return;
        }

        const horizontalDistance = Math.abs(event.clientX - pointerStartHorizontal);
        const verticalDistance = Math.abs(event.clientY - pointerStartVertical);

        if (horizontalDistance > dragThresholdInPixels || verticalDistance > dragThresholdInPixels) {
            pointerHasMoved = true;
        }
    };

    const handlePointerEnd = (): void => {
        if (pointerHasMoved) {
            suppressActivationUntilTimestamp = Date.now() + 100;
        }

        pointerIsPressed = false;
        pointerHasMoved = false;
    };

    panZoomInstance = panzoom(panZoomContainerElement, {
        bounds: true,
        boundsPadding: 0.08,
        maxZoom: 4,
        minZoom: 1,
        smoothScroll: false,
        zoomDoubleClickSpeed: 1,
        beforeMouseDown: (event: MouseEvent) => {
            return controlButtonsElement.contains(event.target as Node);
        },
    }) as PanZoomInstance;

    setPanEnabled(false);
    bindControlButton('zoom-in', () => zoomToViewportCenter('in'));
    bindControlButton('zoom-out', () => zoomToViewportCenter('out'));
    bindControlButton('reset-zoom', resetZoom);

    const initialTransform = panZoomInstance.getTransform();

    if (initialTransform.scale === 1) {
        centerMap(initialTransform.scale);
    }

    panZoomInstance.on('transform', () => {
        scheduleTransformSync();
    });

    mapElement.addEventListener('pointerdown', handlePointerDown);
    mapElement.addEventListener('pointermove', handlePointerMove);
    mapElement.addEventListener('pointerup', handlePointerEnd);
    mapElement.addEventListener('pointercancel', handlePointerEnd);
    mapElement.addEventListener('pointerleave', handlePointerEnd);

    window.addEventListener('resize', () => {
        if (resizeTimeoutId !== null) {
            window.clearTimeout(resizeTimeoutId);
        }

        if (panZoomInstance?.getTransform().scale > 1) {
            lastVisualCenter = getVisualCenter();
        }

        resizeTimeoutId = window.setTimeout(() => {
            restoreVisualCenterAfterResize();
        }, 250);
    });

    return {
        isActivationSuppressed: (): boolean => {
            return Date.now() < suppressActivationUntilTimestamp;
        },
    };
};

export default usePanZoom;
